import CallError from '../../utils/CallError';
import { settings } from './setup';

export function mapECItemToCardObject(data) {
  return {
    ...data,
    price: data.pricingInfo?.price.toFixed(2),
    subscription: data.pricingInfo?.purchaseType === 'SUBSCRIPTION' ? { days: data.pricingInfo?.days } : null,
  };
}

/**
 * Search apps
 * @param {object} params - an object of params
 * @param {string} [params.q] - term to be searched in apps
 * @param {string} [params.qFor] - metadata field to search on. Available values : DESCRIPTION, TAGS, APP_TITLE, PUBLISHER_NAME, ALL. Default value : ALL
 * @param {boolean} [params.featured] - whether or not to return featured apps. If true then all other optional query params will be ignored
 * @param {string[]} [params.appId] - list of appIDS
 * @param {string} [params.sortBy] - sort criteria. Available values : RELEVANCE, PUBLISH_DATE, APP_TITLE, PUBLISHER_NAME. Default value : RELEVANCE
 * @param {string} [params.sortOrder] - sort order. Available values : ASC, DSC. Default value : ASC
 * @param {string} [params.hostApp] - product type within with the search should take place. Default value: All
 * @param {number} [params.pageSize] - size of paginated response. Default value: 12
 * @param {number} [params.pageIndex] - page index of results. Default value: 0
 * @param {string[]} [params.appType] - array of app types for which to search apps
 * @param {string[]} [params.industry] - array of industries for which to search apps
 * @param {string[]} [params.country] - array of countries for which to search apps
 * @param {string[]} [params.filterKeys] - array of filters
 * @param {boolean} [params.fullDetails] - set to true to get all app details, otherwise only card details will be returned
 */
export default async (params = {}, signal = null) => {
  const query = new URLSearchParams();
  const { fullDetails, filterKeys, cloud = 'EC', authToken, ...queryParams } = params;

  Object.entries(queryParams).forEach(([key, value]) => {
    const urlKey = filterKeys?.includes(key) && !['q', 'partnerLevel', 'appType'].includes(key) ? `filter.${key}` : key;
    if (value instanceof Array) {
      queryParams[key].forEach((e) => query.append(urlKey, e));
    } else {
      query.append(urlKey, value.toString());
    }
  });

  const headers = new Headers({
    'x-api-key': settings.apiKey,
    Accept: fullDetails ? 'application/vnd.adobe.xt.app-detail-v2+json' : 'application/vnd.adobe.xt.app-card-v1+json',
  });

  if (authToken) {
    headers.append('Authorization', authToken);
  }

  const response = await fetch(`${settings.endpoint}/v2/apps?type=${cloud}&${query.toString()}`, {
    signal,
    headers,
  });
  let content = await response.text();
  try {
    content = JSON.parse(content);
  } catch (e) {
    // response is not json
  }

  if (!response.ok) {
    throw new CallError('http', content, response.status, { message: response.statusText });
  } else {
    const count = content?._metadata?.totalCount ?? content?.cloudApps?.length ?? 0;
    const pageSize = content?._metadata?.pageSize ?? content?.cloudApps?.length ?? params?.pageSize ?? 1;
    return {
      cloudApps: fullDetails ? content?.cloudApps : content?.cloudApps.map(mapECItemToCardObject),
      products: content?.filters?.filter((e) => e.type === 'hostApp').map(({ name, count }) => ({ name, count })),
      categories: content?.filters
        .filter((e) => !['hostApp', 'partnerLevel'].includes(e.type))
        .reduce((cats, { type, name, count }) => {
          if (cats[type] === undefined) {
            cats[type] = [];
          }
          return { ...cats, [type]: [...cats[type], { name, count }] };
        }, {}),
      totalCount: content?._metadata?.totalCount ?? 0,
      pages: Math.ceil(count / pageSize),
    };
  }
};
